import { FileText, Settings, Play } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { HeroSection } from "@/components/brand/HeroSection";
import { HeroOverline } from "@/components/brand/HeroOverline";
import type { Project, AgentPipelineEntry, Artifact } from "@/types/api";
import { formatDate } from "@/lib/datetime";
import { useAuthStore } from "@/store/authStore";
import { canReachProjectRoute } from "@/lib/project-nav";
import { AGENT_DISPLAY_NAMES, getRouteFromAgentId } from "@/lib/agent-ids";
import { compactTokens } from "@/lib/format";
import { useTranslation } from "react-i18next";

interface ProjectHeaderBannerProps {
  project: Project;
  agents: AgentPipelineEntry[];
  computedPhase: string | null;
  artifacts?: Artifact[];
  /** `workspace_name` do tenant. Nullable: sem nome configurado, o campo sai. */
  workspaceName?: string | null;
  totalTokens?: number;
  totalCost?: number;
  modelLabel?: string | null;
  embeddingModelLabel?: string | null;
}

function projectInitials(name: string) {
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase() || "PR";
}

export function ProjectHeaderBanner({
  project,
  agents,
  computedPhase,
  artifacts = [],
  workspaceName,
  totalTokens = 0,
  totalCost = 0,
  modelLabel,
  embeddingModelLabel,
}: ProjectHeaderBannerProps) {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const completedCount = agents.filter((a) => a.pipeline_status === "completed").length;
  // O pipeline sempre devolve a lista inteira de agentes; vazia, ele ainda não
  // respondeu, e não há total a mostrar.
  const totalCount = agents.length;
  const hasPipeline = totalCount > 0;
  const pct = hasPipeline ? Math.round((completedCount / totalCount) * 100) : 0;
  // O ponto do overline pulsa enquanto algum agente roda, e só aí (#1042).
  const anyAgentRunning = agents.some((a) => a.pipeline_status === "running");

  const artifactAgents = [...new Set(artifacts.map((a) => AGENT_DISPLAY_NAMES[a.agent_id] ?? a.agent_id))]
    .sort()
    .join(" · ");
  const workspaceLabel = workspaceName?.trim();

  // S-009: "next agent" é o primeiro do pipeline (ordem ascendente) cujo
  // pipeline_status não é "completed". Cobre 4 casos:
  //   - Nada rodado ainda → primeiro agent
  //   - Alguns completos → próximo da fila
  //   - Run em curso ("running") → mostra a running pra usuário acompanhar
  //   - Tudo completo → null (botão fica disabled com tooltip)
  const nextAgent = [...agents]
    .sort((a, b) => a.order - b.order)
    .find((a) => a.pipeline_status !== "completed");

  const handleRunNextAgent = () => {
    if (!nextAgent) return;
    const slug = getRouteFromAgentId(nextAgent.agent_id);
    navigate(`/projects/${project.id}/agents/${slug}`);
  };

  const handleReports = () => {
    navigate(`/projects/${project.id}/artifacts`);
  };

  const handleConfigure = () => {
    navigate(`/projects/${project.id}/settings`);
  };

  // SVG donut: 2π * 52 ≈ 326.72
  const circumference = 326.72;
  const dashOffset = circumference - (circumference * pct) / 100;

  // Sem pipeline não há o que afirmar. Com pipeline e sem fase são DOIS casos:
  // nenhum agente concluiu (o projeto não começou), ou algum concluiu sem
  // alcançar fase. O Discover exige o Functional, e nada impede rodar o
  // Architect antes. No segundo, "não começou" contradiz o contador ao lado.
  const phaseLabel = !hasPipeline
    ? "—"
    : computedPhase
      ? `${computedPhase.charAt(0).toUpperCase() + computedPhase.slice(1)} ${t("phase")}`
      : completedCount > 0
        ? "—"
        : t("Not started");

  return (
    <HeroSection padding="comfortable">
      <div className="flex justify-between items-start gap-8 mb-6 flex-wrap">
        <div className="flex items-start gap-5 min-w-0">
          <div
            className="w-[72px] h-[72px] rounded-2xl flex items-center justify-center font-brand font-extrabold text-[26px] tracking-[-0.02em] text-[#ffc83d] flex-shrink-0 relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, rgba(255,200,61,0.2), rgba(255,200,61,0.05))",
              border: "1px solid rgba(255,200,61,0.3)",
            }}
          >
            <span
              className="absolute inset-[-1px] rounded-2xl pointer-events-none"
              style={{ background: "radial-gradient(circle at 30% 30%, rgba(255,200,61,0.25), transparent 60%)" }}
            />
            <span className="relative">{projectInitials(project.name)}</span>
          </div>

          <div className="min-w-0">
            <HeroOverline live={anyAgentRunning} className="mb-2">
              {t("Project")} · {project.status.toUpperCase()} · ID {project.id.slice(0, 8)}
            </HeroOverline>
            <h1 className="font-brand font-extrabold text-[40px] leading-[1.05] tracking-[-0.025em] text-white m-0 mb-2.5 flex items-center gap-3.5 flex-wrap">
              {project.name}
            </h1>
            <div className="flex items-center gap-4 flex-wrap text-xs text-white/55 font-mono tracking-[0.04em]">
              <span>
                <span className="text-white/40 mr-1">{t("Created")}</span>
                <strong className="text-white/90 font-medium">{formatDate(project.created_at)}</strong>
              </span>
              <span className="text-white/25">·</span>
              {workspaceLabel && (
                <>
                  <span>
                    <span className="text-white/40 mr-1">{t("Workspace")}</span>
                    <strong className="text-white/90 font-medium">{workspaceLabel}</strong>
                  </span>
                  <span className="text-white/25">·</span>
                </>
              )}
              <span>
                <span className="text-white/40 mr-1">{t("Methodology")}</span>
                <strong className="text-white/90 font-medium">SAP Activate</strong>
              </span>
            </div>
          </div>
        </div>

        <div className="flex gap-2.5 items-center">
          <Button
            variant="ghost"
            className="h-[38px] bg-white/[0.08] hover:bg-white/[0.14] text-white border border-white/[0.18] gap-2 px-4"
            onClick={handleReports}
            title="View artifacts generated by the agents"
          >
            <FileText className="w-3.5 h-3.5" /> {t("Reports")}
          </Button>
          {/* "Configure" leva a `/projects/:id/settings`, que é `manager`. Sem
              este gate o consultor via o botão e o clique o devolvia para a lista
              de projetos — pior que não ter botão. O mínimo vem do mesmo catálogo
              que guarda a rota. */}
          {canReachProjectRoute(user?.role, "settings") && (
            <Button
              variant="ghost"
              className="h-[38px] bg-white/[0.08] hover:bg-white/[0.14] text-white border border-white/[0.18] gap-2 px-4"
              onClick={handleConfigure}
              title={t("Project settings")}
            >
              <Settings className="w-3.5 h-3.5" /> {t("Configure")}
            </Button>
          )}
          <Button
            variant="action"
            className="h-[38px] gap-2 px-4 font-semibold"
            onClick={handleRunNextAgent}
            disabled={!nextAgent}
            title={nextAgent ? `${t("Open")} ${nextAgent.name}` : t("All agents completed")}
          >
            <Play className="w-3.5 h-3.5" /> {t("Run Next Agent")}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pt-6 border-t border-white/[0.08] items-center">
        <div className="flex items-center gap-5">
          <div className="relative w-[120px] h-[120px] flex-shrink-0">
            <svg width="120" height="120" viewBox="0 0 120 120" style={{ transform: "rotate(-90deg)" }}>
              <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="10" />
              <circle
                cx="60"
                cy="60"
                r="52"
                fill="none"
                stroke="#ffc83d"
                strokeWidth="10"
                strokeDasharray={circumference}
                strokeDashoffset={dashOffset}
                strokeLinecap="round"
                style={{ filter: "drop-shadow(0 0 8px rgba(255,200,61,0.5))", transition: "stroke-dashoffset 0.6s ease" }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
              <div className="font-brand font-extrabold text-[34px] tracking-[-0.03em] text-[#ffc83d] leading-none">
                {hasPipeline ? `${pct}%` : "—"}
              </div>
              <div className="font-mono text-[10px] text-white/50 tracking-[0.1em] mt-0.5">{t("Readiness").toUpperCase()}</div>
            </div>
          </div>
          <div>
            <div className="font-mono text-[10px] text-white/50 tracking-[0.18em] uppercase mb-1.5">
              {t("Go-Live Readiness")}
            </div>
            <h3 className="font-brand font-bold text-lg tracking-[-0.01em] m-0 mb-1 text-white">
              {phaseLabel}
            </h3>
            {hasPipeline && (
              <div className="text-xs text-white/60 leading-[1.45] max-w-[220px]">
                {t("{{count}} of {{total}} agents completed.", { count: completedCount, total: totalCount })}
              </div>
            )}
          </div>
        </div>

        <HeroStat
          label={t("Agents Completed")}
          value={hasPipeline ? String(completedCount) : "—"}
          unit={hasPipeline ? `/ ${totalCount}` : undefined}
          sub={
            hasPipeline
              ? t("{{count}} pending across phases", { count: totalCount - completedCount })
              : undefined
          }
        />
        <HeroStat
          label={t("Artifacts Generated")}
          value={String(artifacts.length)}
          sub={artifactAgents || undefined}
        />
        {/* O `sub` mostra só os modelos que o backend confirmou. Aqui havia o
            nome de dois modelos cravado em JSX — um valor que ninguém consulta
            nunca fica visivelmente desatualizado, e esse chegou a ficar duas
            gerações atrás do que a plataforma roda. O gate que impede o literal
            de voltar está em `agent-model-label.test.tsx`, e ele também não
            deixa citá-lo aqui: por isso a descrição em vez do valor. */}
        <HeroStat
          label={t("Tokens · Spend")}
          value={compactTokens(totalTokens)}
          unit={`· $${totalCost.toFixed(2)}`}
          sub={[modelLabel, embeddingModelLabel].filter(Boolean).join(" · ") || undefined}
        />
      </div>
    </HeroSection>
  );
}

function HeroStat({
  label,
  value,
  unit,
  sub,
}: {
  label: string;
  value: string;
  unit?: string;
  sub?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="font-mono text-[10px] text-white/50 tracking-[0.14em] uppercase">{label}</div>
      <div className="font-brand font-extrabold text-[34px] leading-none tracking-[-0.025em] text-white flex items-baseline gap-1.5">
        {value}
        {unit && <span className="text-sm text-white/40 font-semibold tracking-normal">{unit}</span>}
      </div>
      {sub && <div className="text-[11px] text-white/55 font-mono tracking-[0.04em] mt-0.5">{sub}</div>}
    </div>
  );
}
